import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { initMapFactory, initMultiMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { dateRangeOverlap, maxDate, minDate } from "../../common/utils/dateUtils";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = ma => `${ma.meetingid}_${ma.attendee}`;

const meetingFields = ['title', 'location', 'start', '"end"', {local: 'meeting_tz', foreign: 'tz'}];

export const meetingAttendeeMgrSlice = createSlice({
    name: 'meetingAttendees',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            action.payload.keys.forEach(k => delete state.data[key(k)]);
            action.payload.data.forEach(ma => state.data[key(ma)] = ma);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(ma => state.data[key(ma)] = ma);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(ma => delete state.data[key(ma)]);
        },
        updateMeeting: (state, action) => {
            ensureArray(action.payload).forEach(meeting => {
                const toUpdate = Object.keys(state.data).filter(key => key.startsWith(`${meeting.id}_`));
                toUpdate.forEach(key => {
                    const ma = state.data[key];
                    meetingFields.forEach(f => {
                        if (typeof f === 'string') {
                            ma[f] = meeting[f]
                        } else {
                            ma[f.local] = meeting[f.foreign]
                        }
                    });
                })
            });
        },
        removeMeeting: (state, action) => {
            ensureArray(action.payload).forEach(meetingid => {
                const toRemove = Object.keys(state.data).filter(key => key.startsWith(`${meetingid}_`));
                toRemove.forEach(key => delete state.data[key]);
            });
        },
        removeMember: (state, action) => {
            ensureArray(action.payload).forEach(memberid => {
                const toRemove = Object.keys(state.data).filter(key => key.startsWith(`_${memberid}`));
                toRemove.forEach(key => delete state.data[key]);
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => meetingAttendeeMgrSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'meetingAttendee', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'meetingAttendee');

const fetchMeetingAttendee = aggregatingFetch(
    (key) => network => network.post('/api/v2/meetingAttendees', key, data => meetingAttendeeMgrSlice.actions.set({ keys: [key], data }),visualizeFailure('get') ),
    (keys) => network => network.post('/api/v2/meetingAttendees', keys, data => meetingAttendeeMgrSlice.actions.set({ keys, data }), visualizeFailure('get'))
);
const fetchMeetingAttendeesInRange = aggregatingFetch(
    (start, end) => network => network.post(`/api/v2/meeting/member/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, null, meetingAttendeeMgrSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.post(`/api/v2/meeting/member/${encodeURIComponent(minDate(keys, 0))}/${encodeURIComponent(maxDate(keys, 1))}`, null, meetingAttendeeMgrSlice.actions.addOrUpdate, visualizeFailure('get'))
);

const fetchMemberMeetingsInRange = aggregatingFetch(
    (memberid, start, end) => network => network.post(`/api/v2/meeting/member/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, [memberid], meetingAttendeeMgrSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.post(`/api/v2/meeting/member/${encodeURIComponent(minDate(keys, 1))}/${encodeURIComponent(maxDate(keys, 2))}`, keys.map(k => k[0]), meetingAttendeeMgrSlice.actions.addOrUpdate, visualizeFailure('get'))
);

const fetchAttendeesByMeeting = aggregatingFetch(
    meetingid => network => network.post('/api/v2/meetingAttendeesByMeeting', [meetingid], meetingAttendeeMgrSlice.actions.addOrUpdate, visualizeFailure('get')),
    meetingids => network => network.post('/api/v2/meetingAttendeesByMeeting', meetingids, meetingAttendeeMgrSlice.actions.addOrUpdate, visualizeFailure('get'))
)

export const addMeetingAttendees = (network, meetingid, data) => network.put(`/api/v2/meetingAttendee/${encodeURIComponent(meetingid)}`, ensureArray(data), meetingAttendeeMgrSlice.actions.addOrUpdate, visualizeFailure('create'))
export const updateMeetingAttendee = (network, data) => network.post('/api/v2/meetingAttendee', data, meetingAttendeeMgrSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeMeetingAttendee = (network, data) => network.delete(`/api/v2/meetingAttendee/${encodeURIComponent(data.meetingid)}/${encodeURIComponent(data.attendee)}`, meetingAttendeeMgrSlice.actions.remove, visualizeFailure('remove'));
export const removeMeetingAttendees = (network, meetingid, data) => network.post(`/api/v2/removeMeetingAttendees/${encodeURIComponent(meetingid)}`, data, meetingAttendeeMgrSlice.actions.remove, visualizeFailure('remove'))
// #endregion

// #region selectors
const selectMeetingAttendees = state => state.meetingAttendees.data;
const selectMeetingAttendee = (memberid, meetingid) => createSelector([selectMeetingAttendees], meetingAttendees => meetingAttendees[`${meetingid}_${memberid}`]);
const selectMeetingAttendeesById = keys => createSelector([selectMeetingAttendees], meetingAttendees => keys.reduce((result, key) => {
    const strKey = `${key[1]}_${key[0]}`;
    if (meetingAttendees[strKey]) {
        result.push(meetingAttendees[strKey]);
    }
    return result;
}, []));
const selectMeetingAttendeesInRange = (start, end) => createSelector([selectMeetingAttendees], meetingAttendees => Object.values(meetingAttendees).filter(ma => dateRangeOverlap(ma, { start, end })));
const selectMemberMeetingsInRange = (memberid, start, end) => createSelector([selectMeetingAttendees], meetingAttendees => Object.values(meetingAttendees).filter(ma => ma.attendee === memberid && dateRangeOverlap(ma, { start, end })));
const selectAttendeesByMeeting = meetingid => createSelector([selectMeetingAttendees], attendees => Object.values(attendees).filter(a => a.meetingid === meetingid));
// #endregion

const listen = {
    meetingAttendeeAdminAdded: (attendee, { dispatch, websocket }) => {
        markFetched(websocket, 'meeting_attendee', attendee.attendee, attendee.meetingid)
        dispatch(meetingAttendeeMgrSlice.actions.addOrUpdate(attendee))
    },
    meetingAttendeeAdminUpdated: (attendee, { dispatch }) => dispatch(meetingAttendeeMgrSlice.actions.addOrUpdate(attendee)),
    meetingAttendeeAdminRemoved: (attendee, { dispatch }) => dispatch(meetingAttendeeMgrSlice.actions.remove(attendee)),
    meetingUpdated: (meeting , { dispatch }) => dispatch(meetingAttendeeMgrSlice.actions.updateMeeting(meeting)),
    meetingRemoved: (meeting , { dispatch }) => dispatch(meetingAttendeeMgrSlice.actions.removeMeeting(meeting)),
    memberAdminRemoved: (member, { dispatch }) => dispatch(meetingAttendeeMgrSlice.actions.removeMember(member))
}

const fullRoles = ['administrator', 'event manager', 'training manager', 'meeting manager', 'template manager'];
const readRoles = ['auditor', 'event auditor', 'template auditor', 'training auditor', 'meeting auditor', 'rostering manager', 'rostering auditor', 'recurring rostering manager', 'recurring rostering auditor'];
const options = {
    cleanup: { root: meetingAttendeeMgrSlice.name, all: state => Object.values(selectMeetingAttendees(state)), removeAll: meetingAttendeeMgrSlice.actions.remove, key },
    condition: credentials => credentials.hasRole(fullRoles.concat(readRoles))
}

const meetingAttendeeInitByID = initMapFactory(fetchMeetingAttendee, listen);
registerEntityBasedFetch('meeting_attendee', meetingAttendeeInitByID);

export const [useMgrMeetingAttendee] = dataHookWithCredentialsFactory(selectMeetingAttendee, meetingAttendeeInitByID, { ...options, defaultValue: null });
export const [useMgrMeetingAttendees] = dataHookWithCredentialsFactory(selectMeetingAttendeesById, initMultiMapFactory(meetingAttendeeInitByID, listen), options);
export const [useMgrMeetingAttendeesInRange] = dataHookWithCredentialsFactory(selectMeetingAttendeesInRange, queryMapFactory(fetchMeetingAttendeesInRange, ['meetingid','attendee'], meetingAttendeeInitByID, listen), options);
export const [useMgrMemberMeetingsInRange] = dataHookWithCredentialsFactory(selectMemberMeetingsInRange, queryMapFactory(fetchMemberMeetingsInRange, ['meetingid','attendee'], meetingAttendeeInitByID, listen), options);
export const [useMgrMeetingAttendeesByMeetingId] = dataHookWithCredentialsFactory(selectAttendeesByMeeting, queryMapFactory(fetchAttendeesByMeeting, ['meetingid','attendee'], meetingAttendeeInitByID, listen), options);

const reducer = { name: meetingAttendeeMgrSlice.name, reducer: meetingAttendeeMgrSlice.reducer }
export default reducer;